import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Card, PageHeader, Pill } from '../components/ui'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

interface Ticket {
  id: string; ticket_number: string; subject: string; description: string; priority: 'normal' | 'urgent'
  status: 'open' | 'acknowledged' | 'resolved'; org_name: string; notification_count: number
  last_notified_at: string | null; created_at: string
}

function base64Bytes(value: string): Uint8Array {
  const padding = '='.repeat((4 - value.length % 4) % 4)
  const raw = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

export default function SupportCenter() {
  const authFetch = useAuthFetch()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [admin, setAdmin] = useState(false)
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [pushConfig, setPushConfig] = useState<{ eligible: boolean; configured: boolean; public_key: string | null } | null>(null)

  const load = useCallback(async () => {
    const [tr, pr] = await Promise.all([
      authFetch(`${API}/api/v1/support`), authFetch(`${API}/api/v1/support/push/config`),
    ])
    if (tr.ok) { const data = (await tr.json()).data; setTickets(data.tickets); setAdmin(data.admin) }
    if (pr.ok) setPushConfig((await pr.json()).data)
  }, [authFetch])
  useEffect(() => { load().catch(() => setNotice('Support status could not be loaded.')) }, [load])

  async function createTicket(e: FormEvent) {
    e.preventDefault(); setSaving(true); setNotice(null)
    try {
      const r = await authFetch(`${API}/api/v1/support`, { method: 'POST', body: JSON.stringify({ subject, description, priority: urgent ? 'urgent' : 'normal' }) })
      const json = await r.json()
      if (!r.ok) throw new Error(json.error || 'Ticket could not be opened.')
      setSubject(''); setDescription(''); setUrgent(false)
      setNotice(`Support ticket #${json.data.ticket_number} is open. The owner alert cycle has started.`)
      await load()
    } catch (err: any) { setNotice(err.message) } finally { setSaving(false) }
  }

  async function setStatus(id: string, status: 'acknowledged' | 'resolved') {
    const r = await authFetch(`${API}/api/v1/support/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) })
    if (r.ok) { setNotice(status === 'acknowledged' ? 'Ticket acknowledged. Repeating alerts stopped.' : 'Ticket resolved.'); await load() }
  }

  async function enablePush() {
    if (!pushConfig?.public_key) return
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') throw new Error('Notification permission was not granted.')
      const registration = await navigator.serviceWorker.register('/support-sw.js')
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64Bytes(pushConfig.public_key) as BufferSource })
      const raw = subscription.toJSON()
      const r = await authFetch(`${API}/api/v1/support/push/subscribe`, { method: 'POST', body: JSON.stringify({ ...raw, user_agent: navigator.userAgent }) })
      if (!r.ok) throw new Error((await r.json()).error || 'This device could not be enrolled.')
      setNotice('Owner alerts are enabled on this device.')
    } catch (err: any) { setNotice(err.message) }
  }

  return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
    <PageHeader title="Support Desk" subtitle="Open a ticket. Urgent issues enter the owner alert cycle immediately." actions={
      pushConfig?.eligible ? <button className="btn btn-ghost" disabled={!pushConfig.configured} onClick={enablePush}>{pushConfig.configured ? 'Enable owner alerts' : 'Push setup pending'}</button> : undefined
    } />
    <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) minmax(0, 1fr)', gap: 20 }} className="support-shell">
        <Card style={{ padding: 20, alignSelf: 'start' }}>
          <div className="mono" style={{ color: 'var(--accent)', fontSize: 10, letterSpacing: '.14em', marginBottom: 8 }}>OPEN A TICKET</div>
          <h2 style={{ fontFamily: 'var(--display)', fontWeight: 400, color: 'var(--text-1)', margin: '0 0 8px' }}>What needs attention?</h2>
          <p style={{ color: 'var(--text-2)', fontSize: 12, lineHeight: 1.55, margin: '0 0 18px' }}>Include the location, screen, and what you expected to happen. Your ticket remains visible here through resolution.</p>
          <form onSubmit={createTicket} style={{ display: 'grid', gap: 12 }}>
            <label style={{ display: 'grid', gap: 5, color: 'var(--text-2)', fontSize: 11 }}>Subject<input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short description" maxLength={160} style={inputStyle} /></label>
            <label style={{ display: 'grid', gap: 5, color: 'var(--text-2)', fontSize: 11 }}>Details<textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell us what happened…" rows={7} maxLength={5000} style={{ ...inputStyle, resize: 'vertical' }} /></label>
            <label style={{ display: 'flex', gap: 9, alignItems: 'center', color: 'var(--text-1)', fontSize: 12 }}><input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> Urgent — operations are blocked</label>
            <button className="btn btn-primary" disabled={saving || subject.trim().length < 3 || description.trim().length < 10}>{saving ? 'Opening ticket…' : 'Open support ticket'}</button>
          </form>
          {notice && <div role="status" style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--accent-soft)', color: 'var(--text-1)', fontSize: 11.5, lineHeight: 1.5 }}>{notice}</div>}
        </Card>
        <div>
          <div className="mono" style={{ color: 'var(--text-3)', fontSize: 10, letterSpacing: '.14em', margin: '3px 0 10px' }}>{admin ? 'ALL CUSTOMER TICKETS' : 'YOUR TICKETS'}</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {tickets.map((t) => <Card key={t.id} variant="sm" style={{ padding: 16, borderLeft: `3px solid ${t.status === 'open' ? (t.priority === 'urgent' ? 'var(--danger)' : 'var(--accent)') : 'var(--border-2)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="mono" style={{ color: 'var(--text-3)', fontSize: 10 }}>#{t.ticket_number}</span>
                <Pill tone={t.status === 'open' ? (t.priority === 'urgent' ? 'danger' : 'accent') : t.status === 'acknowledged' ? 'warning' : 'neutral'}>{t.status.toUpperCase()}</Pill>
                {admin && <span style={{ color: 'var(--text-2)', fontSize: 11 }}>{t.org_name}</span>}
                <span className="mono" style={{ marginLeft: 'auto', color: 'var(--text-3)', fontSize: 9 }}>{new Date(t.created_at).toLocaleString()}</span>
              </div>
              <div style={{ color: 'var(--text-1)', fontWeight: 700, marginTop: 10 }}>{t.subject}</div>
              <div style={{ color: 'var(--text-2)', fontSize: 12, lineHeight: 1.55, marginTop: 5, whiteSpace: 'pre-wrap' }}>{t.description}</div>
              {t.status === 'open' && t.notification_count > 0 && <div className="mono" style={{ color: 'var(--text-3)', fontSize: 9, marginTop: 10 }}>OWNER ALERTS SENT: {t.notification_count} · REPEATS EVERY 3 MIN</div>}
              {admin && t.status !== 'resolved' && <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                {t.status === 'open' && <button className="btn btn-ghost" onClick={() => setStatus(t.id, 'acknowledged')}>Acknowledge</button>}
                <button className="btn btn-primary" onClick={() => setStatus(t.id, 'resolved')}>Resolve ticket</button>
              </div>}
            </Card>)}
            {!tickets.length && <Card variant="sm" style={{ padding: 22, color: 'var(--text-2)', fontSize: 12 }}>All clear. No support tickets are open for this account.</Card>}
          </div>
        </div>
      </div>
    </div>
  </div>
}

const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-2)', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-1)', padding: '9px 10px', font: '12px var(--sans)', outline: 'none' }
